import { createContext, createSignal, onMount, useContext, type JSX } from 'solid-js';
import { ApiClient } from './api';
import {
  AuthError,
  clearCallbackParams,
  completeSignIn,
  decodeIdToken,
  forgetRefreshToken,
  hostedLogoutUrl,
  readCallback,
  refreshTokens,
  revokeRefreshToken,
  startSignIn,
  storeRefreshToken,
  storedRefreshToken,
  type Identity,
  type Tokens,
} from './auth';
import type { AppConfig } from './config';

export type AuthStatus = 'starting' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  config: AppConfig;
  status: () => AuthStatus;
  identity: () => Identity | undefined;
  /** Cognito `sub`; the {uid} path segment of every user-scoped endpoint. */
  uid: () => string;
  api: ApiClient;
  error: () => string | undefined;
  /** Path the user was on before signing in; returned once, then cleared. */
  takeRedirect: () => string | undefined;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>();

/** Re-authentication is attempted this long before the token actually expires. */
const REFRESH_MARGIN_MS = 60_000;

export function AuthProvider(props: { config: AppConfig; children: JSX.Element }): JSX.Element {
  const [status, setStatus] = createSignal<AuthStatus>('starting');
  const [identity, setIdentity] = createSignal<Identity>();
  const [error, setError] = createSignal<string>();
  const [tokens, setTokens] = createSignal<Tokens>();
  const [redirectTo, setRedirectTo] = createSignal<string>();

  function adopt(next: Tokens): void {
    setTokens(next);
    storeRefreshToken(next.refreshToken);
    setIdentity(decodeIdToken(next.idToken));
    setStatus('authenticated');
  }

  /** Drops this tab's session only. Used when a token turns out to be
   *  unusable: the Cognito session stays, so signing back in is one click. */
  function forgetSession(): void {
    forgetRefreshToken();
    setTokens(undefined);
    setIdentity(undefined);
    setStatus('anonymous');
  }

  /** Signing out for real: revoke the refresh token, drop local state, then
   *  hand over to Cognito's /logout so its session cookie goes too. Without
   *  that last step the next sign-in is silent, which does not look like
   *  having signed out at all. */
  async function signOut(): Promise<void> {
    const refreshToken = tokens()?.refreshToken ?? storedRefreshToken();
    forgetSession();
    if (refreshToken) {
      try {
        await revokeRefreshToken(props.config, refreshToken);
      } catch {
        // Best effort: the local session is already gone either way.
      }
    }
    window.location.assign(hostedLogoutUrl(props.config));
  }

  async function currentToken(): Promise<string> {
    const held = tokens();
    if (!held) throw new AuthError('Not signed in');
    if (Date.now() < held.expiresAt - REFRESH_MARGIN_MS) return held.idToken;

    const refreshToken = held.refreshToken ?? storedRefreshToken();
    if (!refreshToken) {
      forgetSession();
      throw new AuthError('The session expired. Sign in again.');
    }
    try {
      const next = await refreshTokens(props.config, refreshToken);
      adopt(next);
      return next.idToken;
    } catch (cause) {
      forgetSession();
      setError('The session expired. Sign in again.');
      throw cause;
    }
  }

  onMount(async () => {
    const callback = readCallback();
    if (callback.error) {
      clearCallbackParams();
      setError(callback.error);
      setStatus('anonymous');
      return;
    }
    if (callback.code && callback.state) {
      try {
        const result = await completeSignIn(props.config, callback.code, callback.state);
        clearCallbackParams();
        adopt(result.tokens);
        setRedirectTo(result.returnTo);
      } catch (cause) {
        clearCallbackParams();
        setError(cause instanceof Error ? cause.message : String(cause));
        setStatus('anonymous');
      }
      return;
    }

    const refreshToken = storedRefreshToken();
    if (!refreshToken) {
      setStatus('anonymous');
      return;
    }
    try {
      adopt(await refreshTokens(props.config, refreshToken));
    } catch {
      forgetRefreshToken();
      setStatus('anonymous');
    }
  });

  const value: AuthContextValue = {
    config: props.config,
    status,
    identity,
    uid: () => identity()?.sub ?? '',
    api: new ApiClient(props.config, currentToken),
    error,
    takeRedirect: () => {
      const target = redirectTo();
      if (target !== undefined) setRedirectTo(undefined);
      return target;
    },
    signIn: () => {
      setError(undefined);
      void startSignIn(props.config, window.location.pathname + window.location.search);
    },
    signOut: () => void signOut(),
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
